import React, { useState } from 'react';
import {
  Button,
  Divider,
  Menu,
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ImageIcon from '@mui/icons-material/Image';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RedoIcon from '@mui/icons-material/Redo';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';

const CohortToolbarActions = ({
  nodesCount,
  isExecuting,
  breakpointNodeId,
  breakpointNodeLabel,
  historyPastLength,
  historyFutureLength,
  layoutDirection,
  viewMode,
  hasActiveCohort,
  showExecutionLog,
  onCreateRootNode,
  onLoadDemo,
  onGenerateLargeDemo,
  onAutoLayout,
  onExecuteGraph,
  onExecuteToBreakpoint,
  onImportJson,
  onSaveGraph,
  onExportJson,
  onExportPng,
  onExportSvg,
  onClearGraph,
  onUndo,
  onRedo,
  onOpenShortcuts,
  onToggleExecutionLog,
  onSetLayoutDirection,
  onSetViewMode,
}) => {
  const [buildMenuAnchor, setBuildMenuAnchor] = useState(null);
  const [filesMenuAnchor, setFilesMenuAnchor] = useState(null);
  const [historyMenuAnchor, setHistoryMenuAnchor] = useState(null);
  const [helpMenuAnchor, setHelpMenuAnchor] = useState(null);

  const buildMenuOpen = Boolean(buildMenuAnchor);
  const filesMenuOpen = Boolean(filesMenuAnchor);
  const historyMenuOpen = Boolean(historyMenuAnchor);
  const helpMenuOpen = Boolean(helpMenuAnchor);

  const closeBuildMenu = () => setBuildMenuAnchor(null);
  const closeFilesMenu = () => setFilesMenuAnchor(null);
  const closeHistoryMenu = () => setHistoryMenuAnchor(null);
  const closeHelpMenu = () => setHelpMenuAnchor(null);

  const runDisabled = nodesCount === 0 || isExecuting;
  const exportDisabled = nodesCount === 0 || isExecuting;
  const saveDisabled = nodesCount === 0 || isExecuting;

  const triggerAfterClose = (closeMenu, callback) => () => {
    closeMenu();
    callback();
  };

  return (
    <Stack spacing={0.9}>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={1}
        sx={{
          flexWrap: 'wrap',
          alignItems: { xs: 'stretch', lg: 'center' },
          '& .MuiButton-root': {
            minWidth: 0,
            borderRadius: 1.5,
            whiteSpace: 'nowrap',
          },
        }}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddCircleOutlineIcon />}
          endIcon={<KeyboardArrowDownIcon />}
          onClick={(event) => setBuildMenuAnchor(event.currentTarget)}
          disabled={isExecuting}
          aria-label="Open build menu"
        >
          Build
        </Button>
        <Menu
          anchorEl={buildMenuAnchor}
          open={buildMenuOpen}
          onClose={closeBuildMenu}
          MenuListProps={{ 'aria-label': 'Build actions' }}
        >
          <MenuItem onClick={triggerAfterClose(closeBuildMenu, onCreateRootNode)} disabled={nodesCount > 0 || isExecuting}>
            <AddCircleOutlineIcon fontSize="small" sx={{ mr: 1 }} />
            Create Cohort
          </MenuItem>
          <MenuItem onClick={triggerAfterClose(closeBuildMenu, onLoadDemo)} disabled={isExecuting}>
            <RocketLaunchIcon fontSize="small" sx={{ mr: 1 }} />
            Load Demo Graph
          </MenuItem>
          <MenuItem onClick={triggerAfterClose(closeBuildMenu, onGenerateLargeDemo)} disabled={isExecuting}>
            <RocketLaunchIcon fontSize="small" sx={{ mr: 1 }} />
            Generate 60 Nodes
          </MenuItem>
          <Divider />
          <MenuItem onClick={triggerAfterClose(closeBuildMenu, onAutoLayout)} disabled={nodesCount === 0 || isExecuting}>
            <AutoFixHighIcon fontSize="small" sx={{ mr: 1 }} />
            Auto-Layout
          </MenuItem>
        </Menu>

        <Button
          size="small"
          variant="contained"
          startIcon={<PlayArrowIcon />}
          onClick={onExecuteGraph}
          disabled={runDisabled}
          aria-label="Execute complete graph"
        >
          Run
        </Button>

        <Tooltip
          title={
            breakpointNodeId
              ? `Execute from root to breakpoint "${breakpointNodeLabel}".`
              : 'Set a breakpoint from a node context menu first.'
          }
        >
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<OutlinedFlagIcon />}
              onClick={onExecuteToBreakpoint}
              disabled={runDisabled || !breakpointNodeId}
              aria-label="Execute to selected breakpoint"
            >
              Breakpoint
            </Button>
          </span>
        </Tooltip>

        <Button
          size="small"
          variant="outlined"
          startIcon={<UploadFileIcon />}
          endIcon={<KeyboardArrowDownIcon />}
          onClick={(event) => setFilesMenuAnchor(event.currentTarget)}
          aria-label="Open files menu"
        >
          Files
        </Button>
        <Menu
          anchorEl={filesMenuAnchor}
          open={filesMenuOpen}
          onClose={closeFilesMenu}
          MenuListProps={{ 'aria-label': 'File actions' }}
        >
          <MenuItem onClick={triggerAfterClose(closeFilesMenu, onSaveGraph)} disabled={saveDisabled}>
            <SaveOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
            Save Cohort
          </MenuItem>
          <MenuItem onClick={triggerAfterClose(closeFilesMenu, onImportJson)} disabled={isExecuting}>
            <UploadFileIcon fontSize="small" sx={{ mr: 1 }} />
            Import JSON
          </MenuItem>
          <Divider />
          <MenuItem onClick={triggerAfterClose(closeFilesMenu, onExportJson)} disabled={exportDisabled}>
            <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
            Export JSON
          </MenuItem>
          <MenuItem onClick={triggerAfterClose(closeFilesMenu, onExportPng)} disabled={exportDisabled}>
            <ImageIcon fontSize="small" sx={{ mr: 1 }} />
            Export PNG
          </MenuItem>
          <MenuItem onClick={triggerAfterClose(closeFilesMenu, onExportSvg)} disabled={exportDisabled}>
            <ImageIcon fontSize="small" sx={{ mr: 1 }} />
            Export SVG
          </MenuItem>
        </Menu>

        <Button
          size="small"
          variant="outlined"
          startIcon={<UndoIcon />}
          endIcon={<KeyboardArrowDownIcon />}
          onClick={(event) => setHistoryMenuAnchor(event.currentTarget)}
          aria-label="Open history menu"
        >
          History
        </Button>
        <Menu
          anchorEl={historyMenuAnchor}
          open={historyMenuOpen}
          onClose={closeHistoryMenu}
          MenuListProps={{ 'aria-label': 'History actions' }}
        >
          <MenuItem onClick={triggerAfterClose(closeHistoryMenu, onUndo)} disabled={historyPastLength === 0 || isExecuting}>
            <UndoIcon fontSize="small" sx={{ mr: 1 }} />
            Undo
          </MenuItem>
          <MenuItem onClick={triggerAfterClose(closeHistoryMenu, onRedo)} disabled={historyFutureLength === 0 || isExecuting}>
            <RedoIcon fontSize="small" sx={{ mr: 1 }} />
            Redo
          </MenuItem>
          <Divider />
          <MenuItem onClick={triggerAfterClose(closeHistoryMenu, onClearGraph)} disabled={nodesCount === 0 || isExecuting}>
            <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} />
            Clear Graph
          </MenuItem>
        </Menu>

        <Button
          size="small"
          variant="text"
          startIcon={<InfoOutlinedIcon />}
          endIcon={<KeyboardArrowDownIcon />}
          onClick={(event) => setHelpMenuAnchor(event.currentTarget)}
          aria-label="Open help menu"
        >
          Help
        </Button>
        <Menu
          anchorEl={helpMenuAnchor}
          open={helpMenuOpen}
          onClose={closeHelpMenu}
          MenuListProps={{ 'aria-label': 'Help actions' }}
        >
          <MenuItem onClick={triggerAfterClose(closeHelpMenu, onOpenShortcuts)}>
            <KeyboardIcon fontSize="small" sx={{ mr: 1 }} />
            Shortcuts
          </MenuItem>
          <MenuItem onClick={triggerAfterClose(closeHelpMenu, onToggleExecutionLog)}>
            <InfoOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
            {showExecutionLog ? 'Hide Log' : 'Show Log'}
          </MenuItem>
        </Menu>

        {hasActiveCohort && viewMode === 'graph' ? (
          <Tooltip title="Return to the saved cohort overview">
            <span>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={viewMode}
                onChange={(_, nextViewMode) => {
                  if (nextViewMode) onSetViewMode(nextViewMode);
                }}
                aria-label="Cohort workspace view"
              >
                <ToggleButton value="list" aria-label="Show saved cohort list" disabled={isExecuting}>
                  <FormatListBulletedIcon fontSize="small" sx={{ mr: 0.5 }} />
                  List
                </ToggleButton>
                <ToggleButton value="graph" aria-label="Show graph view" disabled={isExecuting}>
                  <ViewKanbanIcon fontSize="small" sx={{ mr: 0.5 }} />
                  Graph
                </ToggleButton>
              </ToggleButtonGroup>
            </span>
          </Tooltip>
        ) : null}

        {hasActiveCohort && viewMode === 'graph' ? (
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Tooltip title="Choose how the graph is arranged">
              <ToggleButtonGroup
                size="small"
                exclusive
                value={layoutDirection}
                onChange={(_, nextLayoutDirection) => {
                  if (nextLayoutDirection) onSetLayoutDirection(nextLayoutDirection);
                }}
                aria-label="Layout direction"
              >
                <ToggleButton value="LR" aria-label="Left to right layout">
                  L&rarr;R
                </ToggleButton>
                <ToggleButton value="TB" aria-label="Top to bottom layout">
                  T&rarr;B
                </ToggleButton>
              </ToggleButtonGroup>
            </Tooltip>
          </Stack>
        ) : null}
      </Stack>

      <Typography variant="caption" color="text.secondary">
        Demo data, files, history, and graph controls are grouped into one compact toolbar to keep the workspace focused.
      </Typography>
    </Stack>
  );
};

export default CohortToolbarActions;
